import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { listGames } from "@/app/(api)/api/listGames/listGames.js";
import { cacheGame } from "@/app/(api)/api/getGame/useGame.js";
import type { APIGame, APIUser } from "@rosette/ts/src/business/api/api_schema.js";
import type { QueryClient } from "@tanstack/react-query";


export interface UseGamesResult {
    games: APIGame[] | undefined;
    endOfGames: boolean | undefined;
    gamesLoading: boolean;
    gamesError: Error | null;
}


export function useGames(
    user: APIUser,
    fromGamePublicID: string | null,
    limit: number,
): UseGamesResult {

    const queryClient = useQueryClient();
    const { data, isLoading, error } = useQuery({
        queryKey: ["games", user.publicID, fromGamePublicID, limit],
        queryFn: () => listGames(user.publicID, fromGamePublicID, limit),
        staleTime: 60 * 60 * 1000,
    });

    // Cache games so we don't load them again when
    // redirecting to view more details about them.
    useEffect(() => {
        if (data) {
            for (const game of data.games) {
                cacheGame(queryClient, game);
            }
        }
    }, [queryClient, data]);

    return {
        games: data?.games,
        endOfGames: data?.endOfGames,
        gamesLoading: isLoading,
        gamesError: error,
    };
}


export function invalidateGames(
    queryClient: QueryClient,
    fromGamePublicID: string | null,
    limit: number,
) {
    queryClient.removeQueries({ queryKey: ["games", fromGamePublicID, limit] });
}
